import { Alert, Snackbar } from '@mui/material';
import { useState, ReactNode, useMemo, useCallback } from 'react';
import { SnackbarContext } from './SnackbarContext';

interface SnackbarProviderProps {
  readonly children: ReactNode;
}

/**
 * Provider component for toast notifications using Material-UI Snackbar
 *
 * Features:
 * - Success, error, and info notification variants
 * - Auto-dismiss after 6 seconds
 * - Positioned at bottom center
 * - Accessible with proper ARIA attributes
 *
 * Usage:
 * ```tsx
 * const { showSuccess, showError } = useSnackbar();
 * showSuccess('Offer created successfully!');
 * showError('Failed to create offer');
 * ```
 */
export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'info'>('success');

  const showNotification = useCallback((msg: string, sev: 'success' | 'error' | 'info') => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  }, []);

  const showSuccess = useCallback(
    (msg: string) => showNotification(msg, 'success'),
    [showNotification]
  );
  const showError = useCallback(
    (msg: string) => showNotification(msg, 'error'),
    [showNotification]
  );
  const showInfo = useCallback((msg: string) => showNotification(msg, 'info'), [showNotification]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const contextValue = useMemo(
    () => ({ showSuccess, showError, showInfo }),
    [showSuccess, showError, showInfo]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={severity}
          onClose={handleClose}
          variant="filled"
          sx={{
            width: '100%',
            boxShadow: 3,
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
